// Build history list — past builds for a site, newest first.
//
// Click behaviour matches what the customer actually wants:
//   ready                → open the rendered version in a new tab
//   building/queued/...  → in-app progress page
//   failed/cancelled     → in-app progress page (post-mortem)
//
// Only the current ready draft can be published. Older ready builds remain
// available to preview; saved-version restoration happens in the editor.

import { useState } from "react";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type Job } from "../../lib/api";
import { forgePreviewUrl } from "../../lib/domains";

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString();
}

function fmtDuration(start: number, end?: number | null): string {
  if (!end) return "—";
  const sec = Math.round((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

function statusBadge(status: Job["status"]): { label: string; cls: string } {
  switch (status) {
    case "ready":      return { label: "Ready",      cls: "good" };
    case "ready_unverified": return { label: "Needs verification", cls: "warn" };
    case "runner_start_failed": return { label: "Start failed", cls: "bad" };
    case "timeout": return { label: "Timed out", cls: "bad" };
    case "retrying": return { label: "Retrying", cls: "warn" };
    case "failed":     return { label: "Failed",     cls: "bad" };
    case "cancelled":  return { label: "Cancelled",  cls: "" };
    case "building":   return { label: "Building",   cls: "warn" };
    case "validating": return { label: "Validating", cls: "warn" };
    case "queued":     return { label: "Queued",     cls: "warn" };
    case "grounding": return { label: "Gathering business evidence", cls: "warn" };
    case "extracting_inspo": return { label: "Gathering inspiration", cls: "warn" };
    case "synthesizing": return { label: "Planning design", cls: "warn" };
    case "scaffolding": return { label: "Preparing pages", cls: "warn" };
    case "shipping":
    case "publishing": return { label: "Preparing preview", cls: "warn" };
    case "delivery_pending": return { label: "Connecting preview", cls: "warn" };
    case "unavailable": return { label: "Build details unavailable", cls: "" };
    default: return { label: "Build details unavailable", cls: "" };
  }
}

export default function SiteBuildHistory() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [previewBuildId, setPreviewBuildId] = useState<string | null>(null);
  const [liveBuildId, setLiveBuildId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    api.listBuilds(siteId)
      .then((r) => {
        setJobs(r.jobs);
        setPreviewBuildId(r.previewBuildId);
        setLiveBuildId(r.liveBuildId);
      })
      .catch((e) => setErr(String(e)));
  }, [siteId]);

  async function makeLive(jobId: string) {
    if (!siteId) return;
    const job = jobs?.find(item => item.id === jobId);
    if (!job || job.id !== previewBuildId || job.metadataUnavailable || job.status === "unavailable") return;
    setPublishing(jobId);
    setErr(null);
    try {
      const updated = await api.publish(siteId, jobId);
      setLiveBuildId(updated.liveBuildId);
    } catch (e: any) {
      setErr(e?.payload?.error || String(e));
    } finally {
      setPublishing(null);
    }
  }

  if (err) return <div className="card" style={{ borderColor: "#fee2e2", color: "#b91c1c" }}>{err}</div>;
  if (!jobs) return <div className="muted">Loading build history…</div>;

  return (
    <div className="stack site-workspace history-page">
      <div className="row between">
        <h1 style={{ margin: 0 }}>Build history</h1>
        <Link to={`/sites/${siteId}`} className="btn">← back to site</Link>
      </div>

      {jobs.length === 0 && (
        <div className="card stack" style={{ gap: 10, padding: 20 }}>
          <strong>No builds yet.</strong>
          <div className="muted" style={{ fontSize: 13 }}>
            Once you build your site, every version will show up here.
          </div>
          <div>
            <button className="btn primary" onClick={() => navigate(`/sites/${siteId}`)}>
              Build my site
            </button>
          </div>
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {jobs.map((job) => {
          const unavailable = job.metadataUnavailable || job.status === "unavailable";
          const badge = statusBadge(unavailable ? "unavailable" : job.status);
          const isPreview = job.id === previewBuildId;
          const isLive    = job.id === liveBuildId;
          const isReady   = !unavailable && (job.status === "ready" || job.status === "ready_unverified");
          const kind = unavailable ? "Retained version" : job.parentSiteId ? "Edit" : "Initial build";
          const previewUrl = forgePreviewUrl(job.id);
          const progressUrl = `/sites/${siteId}/builds/${job.id}`;
          // Uploaded terminal drafts → open the rendered site directly. Anything else
          // (building, failed, etc.) → progress / post-mortem page.
          const rowHref = isReady ? previewUrl : progressUrl;
          const rowExternal = isReady;

          return (
            <div key={job.id} className="card stack" style={{ gap: 10 }}>
              <div className="row between" style={{ alignItems: "flex-start", gap: 12 }}>
                {unavailable ? <div className="stack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
                  <RowHeader kind={kind} isLive={isLive} isPreview={isPreview} />
                  <RowMeta job={job} />
                  <span className="muted">This version remains in your site's history. Its build details could not be loaded.</span>
                </div> : rowExternal ? (
                  <a
                    href={rowHref}
                    target="_blank"
                    rel="noreferrer"
                    className="stack"
                    style={{ gap: 4, flex: 1, minWidth: 0, textDecoration: "none", color: "inherit" }}
                  >
                    <RowHeader kind={kind} isLive={isLive} isPreview={isPreview} />
                    <RowMeta job={job} />
                  </a>
                ) : (
                  <Link
                    to={progressUrl}
                    className="stack"
                    style={{ gap: 4, flex: 1, minWidth: 0, textDecoration: "none", color: "inherit" }}
                  >
                    <RowHeader kind={kind} isLive={isLive} isPreview={isPreview} />
                    <RowMeta job={job} />
                  </Link>
                )}
                <span className={`badge ${badge.cls}`}>{badge.label}</span>
              </div>

              {isReady && !isLive && (
                <div className="row history-actions" style={{ gap: 8, justifyContent: "flex-end" }}>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn"
                  >
                    View this version ↗
                  </a>
                  {isPreview && <button
                    type="button"
                    className="btn"
                    onClick={() => makeLive(job.id)}
                    disabled={publishing === job.id}
                  >
                    {publishing === job.id ? "Publishing…" : "Publish latest draft"}
                  </button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RowHeader({ kind, isLive, isPreview }: { kind: string; isLive: boolean; isPreview: boolean }) {
  return (
    <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <strong style={{ fontSize: 14 }}>{kind}</strong>
      {isLive && <span className="badge good" style={{ fontSize: 11 }}>Live</span>}
      {isPreview && !isLive && <span className="badge warn" style={{ fontSize: 11 }}>Current draft</span>}
    </div>
  );
}

function RowMeta({ job }: { job: Job }) {
  return (
    <>
      <div className="muted" style={{ fontSize: 12 }}>
        {fmtTime(job.createdAt)} · {fmtDuration(job.createdAt, job.endedAt)}
      </div>
      {job.metadataUnavailable && job.recordedStatus && <div className="muted" style={{ fontSize: 12 }}>Last recorded status: {job.recordedStatus.replace(/_/g, " ")}</div>}
      {job.iterationInstruction && (
        <div
          style={{
            fontSize: 13,
            fontStyle: "italic",
            color: "#374151",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={job.iterationInstruction}
        >
          "{job.iterationInstruction}"
        </div>
      )}
    </>
  );
}
